import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { appointmentApi } from '../services/appointmentApi';
import { socketService } from '../services/socket';
import { sendEmodalLinkStart, linkInstruction } from './emodalLinkHandoff';
import type { Appointment } from '../types/appointment';

/**
 * useRescheduleFlow
 *
 * Same shape as useFixPlateFlow but watches for appointment_time change
 * instead of plate_verified_at. The flow:
 *   1. POST /appointments/:id/reschedule-session — backend builds the
 *      eModal edit-mode URL.
 *   2. Send EMODAL_RESCHEDULE_START to the extension; operator advances
 *      Step 2 → 3 manually, picks a new time slot, clicks Submit.
 *   3. Extension forwards receipt to backend → completeReschedule
 *      updates appointment_time + appointment_end + reschedule_count++.
 *   4. Backend emits appointment_updated; this hook compares the new
 *      appointment_time to the original and fires onCompleted on diff.
 */

const EXT_ID = import.meta.env.VITE_CHROME_EXT_ID || 'nniodjlikchoomnhdbcnlfncdcpodbib';

export interface UseRescheduleFlowOptions {
  onCompleted?: (payload: { appointmentId: number; appointment: Appointment }) => void;
  onError?: (payload: { appointmentId: number; reason: string }) => void;
}

function sendEmodalRescheduleStart(payload: {
  sessionId: string;
  appointmentId: number;
  url: string;
  // Lets the assistant tell a loaded eModal edit page from the empty one it
  // renders when the appointment behind the link no longer exists there.
  containerNumber: string | null;
  userJwt: string;
}): Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    if (!window.chrome?.runtime?.sendMessage) {
      resolve({ ok: false, error: 'Chrome runtime unavailable' });
      return;
    }
    try {
      chrome.runtime.sendMessage(
        EXT_ID,
        { type: 'EMODAL_RESCHEDULE_START', ...payload },
        (resp: { ok?: boolean; error?: string } | undefined) => {
          if (chrome.runtime.lastError) {
            resolve({ ok: false, error: chrome.runtime.lastError.message });
          } else {
            resolve({ ok: !!resp?.ok, error: resp?.error });
          }
        }
      );
    } catch (e) {
      resolve({ ok: false, error: (e as Error).message });
    }
  });
}

export function useRescheduleFlow(options: UseRescheduleFlowOptions = {}) {
  const { onCompleted, onError } = options;
  const activeAppointmentRef = useRef<number | null>(null);
  const originalTimeRef = useRef<string | null>(null);
  const activeToastRef = useRef<string | null>(null);
  // See useFixPlateFlow — set while the operator is on eModal locating a
  // hand-entered appointment so we can resume once its IDs are stored.
  const awaitingLinkRef = useRef<number | null>(null);
  const startRef = useRef<((appointmentId: number) => Promise<{ ok: boolean; error?: string }>) | undefined>(undefined);
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);
  useEffect(() => { onCompletedRef.current = onCompleted; }, [onCompleted]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // appointment_updated fires on many edits (fees, hold, plate, …). To
  // distinguish a reschedule we compare appointment_time against what we
  // captured when the flow started — a real reschedule is the only edit
  // that changes appointment_time.
  useEffect(() => {
    const handle = (raw: unknown) => {
      const data = raw as {
        appointment?: {
          id?: number;
          appointment_time?: string | null;
          emodal_truck_visit_id?: number | string | null;
        };
      };
      const a = data.appointment;
      if (!a || !a.id) return;

      // Link landed → resume the reschedule.
      if (awaitingLinkRef.current === a.id) {
        if (!a.emodal_truck_visit_id) return;
        awaitingLinkRef.current = null;
        if (activeToastRef.current) toast.dismiss(activeToastRef.current);
        activeToastRef.current = null;
        void startRef.current?.(a.id);
        return;
      }

      if (activeAppointmentRef.current !== a.id) return;
      const newTime = a.appointment_time || null;
      const oldTime = originalTimeRef.current;
      // Need a different time to count as completion. If somehow the
      // backend emits with the same time, ignore — wait for the real one.
      if (!newTime || newTime === oldTime) return;
      if (activeToastRef.current) toast.dismiss(activeToastRef.current);
      activeAppointmentRef.current = null;
      originalTimeRef.current = null;
      activeToastRef.current = null;
      onCompletedRef.current?.({ appointmentId: a.id, appointment: a as unknown as Appointment });
    };
    socketService.on('appointment_updated', handle);
    return () => { socketService.off('appointment_updated', handle); };
  }, []);

  // eModal opened an appointment it no longer has. Nothing is coming back over
  // appointment_updated for this attempt, so without this the loading toast
  // spins until the assistant's own 10-minute watchdog gives up — which is how
  // "the button does nothing" gets reported. The backend has already started a
  // sync; the operator's job is simply to try again once it lands.
  useEffect(() => {
    const handleStale = (raw: unknown) => {
      const data = raw as { appointmentId?: number; intent?: string };
      if (!data?.appointmentId) return;
      if (data.intent === 'fix_plate') return;
      if (activeAppointmentRef.current !== data.appointmentId
          && awaitingLinkRef.current !== data.appointmentId) return;
      if (activeToastRef.current) toast.dismiss(activeToastRef.current);
      activeToastRef.current = null;
      activeAppointmentRef.current = null;
      originalTimeRef.current = null;
      awaitingLinkRef.current = null;
      toast.error(
        'This appointment was changed on eModal, so the booking we had is gone. '
        + 'Re-reading eModal now — try Reschedule again in a minute.'
      );
      onErrorRef.current?.({ appointmentId: data.appointmentId, reason: 'EMODAL_APPT_STALE_LINK' });
    };
    socketService.on('appointment_link_stale', handleStale);
    return () => { socketService.off('appointment_link_stale', handleStale); };
  }, []);

  // See useFixPlateFlow — call sites discard the return value, so a refusal
  // has to reach the operator as a toast or it reads as "the button does
  // nothing".
  const fail = (appointmentId: number, error: string) => {
    toast.error(error);
    onErrorRef.current?.({ appointmentId, reason: error });
    return { ok: false, error };
  };

  const startReschedule = useCallback(async (appointmentId: number): Promise<{ ok: boolean; error?: string }> => {
    const userJwt = localStorage.getItem('token');
    if (!userJwt) return fail(appointmentId, 'Not signed in');
    if (!window.chrome?.runtime?.sendMessage) {
      return fail(appointmentId, 'EAloom extension not available — install/enable the extension first');
    }

    let session;
    try {
      session = await appointmentApi.createRescheduleSession(appointmentId);
    } catch (err) {
      // Same reason as useFixPlateFlow: the backend's 400 carries the actual
      // instruction ("add the eModal ticket number"), and axios replaces it
      // with "Request failed with status code 400" unless we dig it out.
      return fail(
        appointmentId,
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          || (err as Error).message
          || 'Failed to create reschedule session'
      );
    }

    // Hand-entered appointment: find it on eModal first. The socket
    // handler above resumes this function once the IDs are stored.
    if (session.mode === 'link_required') {
      const linkToastId = `emodal-link-${appointmentId}-${Date.now()}`;
      activeToastRef.current = linkToastId;
      awaitingLinkRef.current = appointmentId;
      toast.loading(linkInstruction(session.search), { id: linkToastId });

      const linkHandoff = await sendEmodalLinkStart({
        sessionId: session.sessionId,
        appointmentId,
        listUrl: session.listUrl,
        search: session.search,
        intent: 'reschedule',
        userJwt,
      });
      if (!linkHandoff.ok) {
        toast.dismiss(linkToastId);
        awaitingLinkRef.current = null;
        activeToastRef.current = null;
        return fail(appointmentId, linkHandoff.error || 'Extension failed to open eModal');
      }
      return { ok: true };
    }

    activeAppointmentRef.current = appointmentId;
    originalTimeRef.current = session.currentAppointmentTime ?? null;
    const toastId = `reschedule-${appointmentId}-${Date.now()}`;
    activeToastRef.current = toastId;
    toast.loading('Opening eModal — please pick a new time slot, then click Submit', { id: toastId });

    const handoff = await sendEmodalRescheduleStart({
      sessionId: session.sessionId,
      appointmentId,
      url: session.url,
      containerNumber: session.containerNumber ?? null,
      userJwt,
    });
    if (!handoff.ok) {
      toast.dismiss(toastId);
      activeAppointmentRef.current = null;
      originalTimeRef.current = null;
      activeToastRef.current = null;
      return fail(appointmentId, handoff.error || 'Extension failed to open eModal');
    }
    return { ok: true };
  }, []);

  // Resume path for the socket handler — see useFixPlateFlow.
  useEffect(() => { startRef.current = startReschedule; }, [startReschedule]);

  return { startReschedule };
}
